<template>
  <div class="banner-list-container">
    <!-- 现代化页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-picture"></i>
              轮播图管理
            </h1>
            <p class="page-subtitle">管理首页轮播图展示，控制图片展示和跳转链接</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button type="primary" icon="el-icon-plus" @click="handleCreate" class="action-btn">
            新增轮播图
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 搜索筛选区域 -->
      <div class="search-section">
        <el-card class="search-card">
          <el-form :model="queryForm" label-width="110px" class="search-form">
            <el-row :gutter="20">
              <el-col :span="6">
                <el-form-item label="轮播图标题">
                  <el-input 
                    v-model="queryForm.keyword" 
                    placeholder="请输入轮播图标题" 
                    clearable 
                    size="medium"
                    @keyup.enter.native="handleSearch"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="4">
                <el-form-item label="状态">
                  <el-select v-model="queryForm.is_active" placeholder="请选择状态" clearable size="medium" style="width: 100%">
                    <el-option label="启用" :value="true">
                      <span style="float: left">启用</span>
                      <span style="float: right; color: #67C23A; font-weight: 600">
                        <i class="el-icon-success"></i> 启用
                      </span>
                    </el-option>
                    <el-option label="禁用" :value="false">
                      <span style="float: left">禁用</span>
                      <span style="float: right; color: #F56C6C; font-weight: 600">
                        <i class="el-icon-error"></i> 禁用
                      </span>
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="链接类型">
                  <el-select v-model="queryForm.link_type" placeholder="请选择链接类型" clearable size="medium" style="width: 100%">
                    <el-option label="无链接" :value="1" />
                    <el-option label="页面跳转" :value="2" />
                    <el-option label="外部链接" :value="3" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item>
                  <el-button type="primary" @click="handleSearch" icon="el-icon-search" size="medium">搜索</el-button>
                  <el-button @click="handleReset" icon="el-icon-refresh" size="medium">重置</el-button>
                  <el-button @click="handleRefresh" icon="el-icon-refresh" size="medium">刷新</el-button>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </el-card>
      </div>

      <!-- 统计卡片 -->
      <div class="stats-section">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon total">
                <i class="el-icon-picture"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ bannerList.length }}</div>
                <div class="stat-label">轮播图总数</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon active">
                <i class="el-icon-success"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ bannerList.filter(b => b.is_active).length }}</div>
                <div class="stat-label">启用中</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon inactive">
                <i class="el-icon-error"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ bannerList.filter(b => !b.is_active).length }}</div>
                <div class="stat-label">已禁用</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon expired">
                <i class="el-icon-time"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ getExpiredCount() }}</div>
                <div class="stat-label">已过期</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 轮播图列表 -->
      <div class="table-section">
        <el-card class="table-card">
          <div slot="header" class="table-header">
            <span class="table-title">
              <i class="el-icon-tickets"></i> 轮播图列表
            </span>
            <span class="table-subtitle">共 {{ total }} 条记录</span>
          </div>

          <el-table 
            :data="bannerList" 
            v-loading="tableLoading" 
            stripe 
            style="width: 100%"
            @selection-change="handleSelectionChange"
            :header-cell-style="{ background: '#f8f9fa' }"
          >
            <el-table-column type="selection" width="55" />
            
            <el-table-column label="轮播图信息" width="250" align="left">
              <template slot-scope="scope">
                <div class="banner-info">
                  <div class="banner-image">
                    <el-image
                      :src="scope.row.image_url"
                      fit="cover"
                      class="banner-thumb"
                      :preview-src-list="[scope.row.image_url]"
                    >
                      <div slot="error" class="image-placeholder">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                  </div>
                  <div class="banner-details">
                    <div class="banner-title">{{ scope.row.title }}</div>
                    <div class="banner-meta">
                      <span class="sort-order">排序: {{ scope.row.sort_order }}</span>
                      <el-tag v-if="scope.row.link_type === 1" size="mini" type="info">无链接</el-tag>
                      <el-tag v-else-if="scope.row.link_type === 2" size="mini" type="primary">页面跳转</el-tag>
                      <el-tag v-else-if="scope.row.link_type === 3" size="mini" type="warning">外部链接</el-tag>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.is_active ? 'success' : 'danger'" size="medium">
                  {{ scope.row.is_active ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="时间范围" width="160" align="center">
              <template slot-scope="scope">
                <div class="time-range">
                  <div v-if="scope.row.start_time" class="time-item">
                    <i class="el-icon-time"></i>
                    {{ formatDate(scope.row.start_time, 'short') }}
                  </div>
                  <div v-if="scope.row.end_time" class="time-item">
                    <i class="el-icon-timer"></i>
                    {{ formatDate(scope.row.end_time, 'short') }}
                  </div>
                  <div v-if="!scope.row.start_time && !scope.row.end_time" class="no-time">
                    永久有效
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="创建时间" width="160" align="center">
              <template slot-scope="scope">
                <div class="create-time">
                  <i class="el-icon-date"></i>
                  {{ formatDate(scope.row.created_at) }}
                </div>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="300" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    size="mini" 
                    type="primary" 
                    icon="el-icon-edit" 
                    @click="handleEdit(scope.row)"
                  >
                    编辑
                  </el-button>
                  <el-button 
                    size="mini" 
                    :type="scope.row.is_active ? 'warning' : 'success'" 
                    :icon="scope.row.is_active ? 'el-icon-close' : 'el-icon-check'"
                    @click="handleToggleStatus(scope.row)"
                  >
                    {{ scope.row.is_active ? '禁用' : '启用' }}
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="danger" 
                    icon="el-icon-delete" 
                    @click="handleDelete(scope.row)"
                  >
                    删除
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <div class="pagination-container">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="queryForm.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="queryForm.page_size"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              background
            />
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { getBanners, deleteBanner, updateBannerStatus } from '@/api/banner'

export default {
  name: 'BannerList',
  data() {
    return {
      queryForm: {
        page: 1,
        page_size: 20,
        keyword: '',
        is_active: null,
        link_type: null
      },
      
      bannerList: [],
      total: 0,
      tableLoading: false,
      selectedBanners: []
    }
  },
  
  created() {
    this.fetchData()
  },
  
  methods: {
    async fetchData() {
      this.tableLoading = true
      try {
        const response = await getBanners(this.queryForm)
        this.bannerList = response.data.list || []
        this.total = response.data.pagination?.total || 0
      } catch (error) {
        console.error('获取轮播图列表失败:', error)
        this.$message.error('获取轮播图列表失败')
      } finally {
        this.tableLoading = false
      }
    },
    
    handleSearch() {
      this.queryForm.page = 1
      this.fetchData()
    },
    
    handleReset() {
      this.queryForm = {
        page: 1,
        page_size: 20,
        keyword: '',
        is_active: null,
        link_type: null
      }
      this.fetchData()
    },
    
    handleRefresh() {
      this.fetchData()
    },
    
    handleCreate() {
      this.$router.push('/admin/banners/create')
    },
    
    handleEdit(banner) {
      this.$router.push(`/admin/banners/${banner.id}/edit`)
    },
    
    async handleToggleStatus(banner) {
      try {
        const newStatus = !banner.is_active
        await updateBannerStatus(banner.id, newStatus)
        banner.is_active = newStatus
        this.$message.success(`轮播图${newStatus ? '启用' : '禁用'}成功`)
      } catch (error) {
        this.$message.error('状态更新失败')
      }
    },
    
    handleDelete(banner) {
      this.$confirm('确定要删除这个轮播图吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteBanner(banner.id)
          this.$message.success('删除成功')
          this.fetchData()
        } catch (error) {
          this.$message.error('删除失败')
        }
      })
    },
    
    handleSelectionChange(selection) {
      this.selectedBanners = selection
    },
    
    handleSizeChange(size) {
      this.queryForm.page_size = size
      this.queryForm.page = 1
      this.fetchData()
    },
    
    handleCurrentChange(page) {
      this.queryForm.page = page
      this.fetchData()
    },
    
    formatDate(date, format = 'full') {
      if (!date) return ''
      const d = new Date(date)
      if (format === 'short') {
        return d.toLocaleDateString('zh-CN')
      }
      return d.toLocaleString('zh-CN')
    },
    
    getExpiredCount() {
      const now = new Date()
      return this.bannerList.filter(banner => {
        return banner.end_time && new Date(banner.end_time) < now
      }).length
    }
  }
}
</script>

<style scoped>
.banner-list-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部样式 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  color: white;
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  color: white;
  opacity: 0.9;
  font-size: 16px;
  margin: 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* 主要内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px 40px;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 30px;
}

.search-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
}

.search-form {
  margin-bottom: 0;
}

/* 统计卡片 */
.stats-section {
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.stat-icon i {
  color: #4facfe;
  z-index: 1;
}

.stat-icon.total::before {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-icon.total i {
  color: #667eea;
}

.stat-icon.active::before {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.active i {
  color: #67c23a;
}

.stat-icon.inactive::before {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.stat-icon.inactive i {
  color: #f56c6c;
}

.stat-icon.expired::before {
  background: linear-gradient(135deg, #e6a23c, #eebe77);
}

.stat-icon.expired i {
  color: #e6a23c;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 30px;
}

.table-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.table-subtitle {
  color: #666;
  font-size: 14px;
}

/* 轮播图信息样式 */
.banner-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.banner-image {
  flex-shrink: 0;
}

.banner-thumb {
  width: 70px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e4e7ed;
}

.banner-thumb:hover {
  transform: scale(1.05);
  border-color: #409eff;
}

.image-placeholder {
  width: 70px;
  height: 40px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 18px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.banner-details {
  flex: 1;
  min-width: 0; /* 防止flex项溢出 */
  overflow: hidden;
}

.banner-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.banner-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  flex-wrap: wrap;
}

.banner-meta .el-tag {
  margin: 0;
  font-size: 10px;
  padding: 0 4px;
  height: 18px;
  line-height: 18px;
}

.sort-order {
  color: #666;
  background: #f0f2f5;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
  white-space: nowrap;
}

/* 时间范围样式 */
.time-range {
  font-size: 12px;
  text-align: center;
}

.time-item {
  color: #666;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.no-time {
  color: #67c23a;
  font-weight: 500;
  text-align: center;
}

/* 创建时间样式 */
.create-time {
  color: #666;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  padding: 8px 4px;
  justify-content: center;
}

.action-buttons .el-button {
  min-width: 60px;
  font-size: 13px;
  padding: 7px 15px;
}

/* 分页样式 */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  padding: 20px 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-content {
    padding: 0 20px 20px;
  }
  
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .stat-card {
    padding: 16px 12px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  /* 移动端表格调整 */
  .banner-info {
    gap: 8px;
  }
  
  .banner-thumb {
    width: 60px;
    height: 35px;
  }
  
  .image-placeholder {
    width: 60px;
    height: 35px;
    font-size: 16px;
  }
  
  .banner-title {
    font-size: 12px;
  }
  
  .banner-meta {
    gap: 4px;
  }
  
  .sort-order {
    font-size: 9px;
    padding: 1px 3px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
    padding: 4px 2px;
  }
  
  .action-buttons .el-button {
    min-width: 50px;
    font-size: 11px;
    padding: 5px 8px;
  }
  
  .time-range {
    font-size: 11px;
  }
  
  .time-item {
    justify-content: center;
  }
  
  .create-time {
    font-size: 11px;
    justify-content: center;
  }
}
</style> 